/**
 * SMS duty service.
 *
 * The legacy binary on/off duty endpoint, shared with AIM
 * (`apps/aegix-aim-react/src/main/services/duty/index.tsx` —
 * `getUserDuty` / `updateUserDuty`):
 *
 *   GET   /api/v2/user-duty/{id}/
 *   PATCH /api/v2/user-duty/{id}/   body { is_on_duty, scheduled_at? }
 *
 * Mirrors AIM's typed `@tiaki/http-client` `client.GET/PATCH` surface. The
 * generated schema declares `path?: never` on this path (the ViewSet pk isn't
 * spelled out in the OpenAPI artifact), so the `{ id } as never` cast on
 * `params.path` matches AIM verbatim — the request still carries the id.
 *
 * SMS only needs this binary toggle; the ADR-126 off-duty windows/exceptions
 * CRUD that AIM also wires (`/api/v2/duty/...`) is out of scope here.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface UserDuty {
  is_on_duty: boolean;
  scheduled_at: string | null;
}

export const getUserDuty = async (id: number): Promise<UserDuty> => {
  const result = await client.GET('/api/v2/user-duty/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as UserDuty;
};

export const updateUserDuty = async (
  id: number,
  payload: { is_on_duty: boolean; scheduled_at?: string | null },
): Promise<UserDuty> => {
  const result = await client.PATCH('/api/v2/user-duty/{id}/', {
    params: { path: { id } as never },
    body: payload as never,
  });
  return unwrap(result) as unknown as UserDuty;
};
